import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Hashids from 'hashids';
import React, { useEffect, useState } from 'react';
import { ChevronRight, Plus as PlusIcon } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  type AdminRole,
  createRole,
  getApiErrorMessage,
  hasPermission,
  listRoles,
} from '@/api/admin-access';
import Breadcrumb from '@/components/common/breadcrumb.component';
import { UserStore } from '@/stores';

const hashids = new Hashids('', 12);

const RolesView = () => {
  const navigate = useNavigate();
  const access = UserStore.useState((state) => state.access);
  const canWrite = hasPermission(access, 'settings', 'write');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [keyEdited, setKeyEdited] = useState(false);

  useEffect(() => {
    listRoles()
      .then(setRoles)
      .catch((error) => toast.error(getApiErrorMessage(error)))
      .finally(() => setLoading(false));
  }, []);

  const openCreateDialog = () => {
    setName('');
    setKey('');
    setDescription('');
    setKeyEdited(false);
    setDialogOpen(true);
  };

  const updateName = (value: string) => {
    setName(value);
    if (!keyEdited) {
      setKey(
        value
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9_-]+/g, '_')
      );
    }
  };

  const handleCreate = async () => {
    if (!name.trim() || !key.trim()) {
      toast.error('Role name and key are required');
      return;
    }
    setSaving(true);
    try {
      const role = await createRole({
        name: name.trim(),
        key: key.trim(),
        description: description.trim(),
        permissions: [],
      });
      toast.success('Role created');
      setDialogOpen(false);
      navigate(`/settings/roles/${hashids.encode(role.role_id)}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Breadcrumb title="Roles" label="Roles" parent="Settings" />

      <div className="container-fluid">
        <Box sx={{ maxWidth: 800, width: '100%', margin: '30px auto' }}>
          <Stack direction="row" justifyContent="flex-end" mb={2}>
            {canWrite && (
              <Button
                variant="contained"
                startIcon={<PlusIcon size={17} />}
                onClick={openCreateDialog}
              >
                Create role
              </Button>
            )}
          </Stack>

          <Card>
            {loading && (
              <Box py={6} textAlign="center">
                <CircularProgress size={28} />
              </Box>
            )}
            {!loading && roles.length === 0 && (
              <Box p={4} textAlign="center">
                <Typography color="text.secondary">
                  No roles have been created yet.
                </Typography>
              </Box>
            )}
            {!loading && roles.length > 0 && (
              <List disablePadding>
                {[...roles]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((role) => (
                    <ListItem
                      key={role.role_id}
                      disablePadding
                      divider
                      secondaryAction={<ChevronRight size={18} />}
                    >
                      <ListItemButton
                        onClick={() =>
                          navigate(
                            `/settings/roles/${hashids.encode(role.role_id)}`
                          )
                        }
                      >
                        <ListItemText
                          primary={
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <span>{role.name}</span>
                              {role.is_system && (
                                <Chip size="small" label="System" />
                              )}
                            </Stack>
                          }
                          secondary={role.description || role.key}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
              </List>
            )}
          </Card>
        </Box>
      </div>

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create role</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              required
              label="Name"
              value={name}
              onChange={(event) => updateName(event.target.value)}
            />
            <TextField
              required
              label="Key"
              value={key}
              helperText="Stable identifier stored with this role."
              onChange={(event) => {
                setKeyEdited(true);
                setKey(event.target.value);
              }}
            />
            <TextField
              label="Description"
              value={description}
              multiline
              minRows={2}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button disabled={saving} variant="contained" onClick={handleCreate}>
            {saving ? 'Creating…' : 'Create role'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RolesView;
